import { LogOut, Monitor, Moon, Sun, UserCog } from 'lucide-react'
import { DEV_ROLES, useAuth, type DevRole } from '@/auth/context'
import { usePermissions } from '@/auth/usePermissions'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useTheme, type ThemePreference } from '@/lib/theme'

const ROLE_HINTS: Record<DevRole, string> = {
  admin: 'Everything, including keys and audit',
  editor: 'Edit content, resend messages',
  viewer: 'Read only',
}

function initials(name: string): string {
  const parts = name
    .replace(/[^\p{L}\s]/gu, ' ')
    .trim()
    .split(/\s+/)
  return (
    (parts[0]?.[0] ?? '?') + (parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '')
  ).toUpperCase()
}

export function UserMenu() {
  const auth = useAuth()
  const { role, me } = usePermissions()
  const { preference, setPreference } = useTheme()
  const name = me?.name || auth.account.name
  const email = me?.username || auth.account.username
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`Account: ${name}`}
          className="flex h-8 cursor-pointer items-center gap-2 rounded-md pr-1.5 pl-1 text-sm hover:bg-surface-3 focus-visible:outline-2 focus-visible:outline-focus"
        >
          <span className="grid size-6 place-items-center rounded-full bg-ink text-[0.625rem] font-semibold text-bg">
            {initials(name)}
          </span>
          {role && (
            <span className="hidden text-xs font-medium text-ink-2 capitalize md:inline">
              {role}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-64">
        <div className="px-2 py-1.5">
          <div className="truncate text-sm font-medium text-ink">{name}</div>
          <div className="truncate text-xs text-ink-2">{email}</div>
        </div>
        <DropdownMenuSeparator />
        {auth.mode === 'dev' && auth.setDevRole && (
          <>
            <DropdownMenuLabel className="flex items-center gap-1.5">
              <UserCog className="size-3.5" /> Act as (dev only)
            </DropdownMenuLabel>
            <DropdownMenuRadioGroup
              value={auth.devRole}
              onValueChange={(v) => auth.setDevRole?.(v as DevRole)}
            >
              {DEV_ROLES.map((r) => (
                <DropdownMenuRadioItem key={r} value={r} onSelect={(e) => e.preventDefault()}>
                  <div>
                    <div className="capitalize">{r}</div>
                    <div className="text-xs text-ink-3">{ROLE_HINTS[r]}</div>
                  </div>
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuLabel>Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup
          value={preference}
          onValueChange={(v) => setPreference(v as ThemePreference)}
        >
          <DropdownMenuRadioItem value="system" onSelect={(e) => e.preventDefault()}>
            <Monitor /> System
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="light" onSelect={(e) => e.preventDefault()}>
            <Sun /> Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark" onSelect={(e) => e.preventDefault()}>
            <Moon /> Dark
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        {auth.mode === 'entra' && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={auth.signOut}>
              <LogOut /> Sign out
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
